import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ConfigQuestion } from '../../models/config-question.interface';
import {
	ControlValueAccessor,
	UntypedFormArray,
	UntypedFormBuilder,
	UntypedFormControl,
	UntypedFormGroup,
	NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { ErrorMessage } from '../../constants/enum.const';

export interface DeclaredWallet {
	network: string;
	address: string;
}

@Component({
	selector: 'bcb-wallet-list-input',
	templateUrl: './wallet-list-input.component.html',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			multi: true,
			useExisting: WalletListInputComponent,
		},
	],
	styleUrls: ['./wallet-list-input.component.scss'],
})
export class WalletListInputComponent
	implements OnInit, ControlValueAccessor
{
	@Input() question!: ConfigQuestion;
	@Input() formGroup?: UntypedFormGroup;
	@Input() formControl?: UntypedFormControl;
	@Input() formControlName?: string;
	@Input() errorMessage?: ErrorMessage;
	@Output() walletsChange: EventEmitter<DeclaredWallet[]> = new EventEmitter<
		DeclaredWallet[]
	>();

	rows: UntypedFormArray = this.fb.array([]);
	isDisabled: boolean = false;

	constructor(private readonly fb: UntypedFormBuilder) {}

	ngOnInit(): void {
		if (this.formControl) {
			this.formGroup = this.formControl.parent as UntypedFormGroup;
		} else if (this.formGroup && this.formControlName) {
			this.formControl = this.formGroup.get(
				this.formControlName
			) as UntypedFormControl;
		} else {
			throw new Error(
				'One of the following is required: [formControl] or [formControlName] + [FormGroup]'
			);
		}

		if (this.rows.length === 0) {
			this.rows.push(this.createRow());
		}
	}

	changed: (value: DeclaredWallet[]) => void = () => {};

	touched: () => void = () => {};

	writeValue(obj: DeclaredWallet[] | null | undefined): void {
		const wallets = Array.isArray(obj) && obj.length > 0 ? obj : [this.emptyRow()];
		this.rows.clear();
		for (const wallet of wallets) {
			this.rows.push(this.createRow(wallet));
		}
	}

	registerOnChange(fn: (value: DeclaredWallet[]) => void): void {
		this.changed = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.touched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.isDisabled = isDisabled;
		if (isDisabled) {
			this.rows.disable({ emitEvent: false });
		} else {
			this.rows.enable({ emitEvent: false });
		}
	}

	addRow(): void {
		this.rows.push(this.createRow());
		this.publish();
		this.touched();
	}

	removeRow(index: number): void {
		if (this.rows.length <= 1) {
			this.rows.at(0).reset(this.emptyRow());
		} else {
			this.rows.removeAt(index);
		}
		this.publish();
		this.touched();
	}

	onNetworkChange(): void {
		this.publish();
	}

	onAddressBlur(): void {
		this.publish();
		this.touched();
	}

	private publish(): void {
		const value = this.trimmedRows();
		value.forEach((wallet, index) => {
			const address = this.rows.at(index)?.get('address');
			if (address && address.value !== wallet.address) {
				address.setValue(wallet.address, { emitEvent: false });
			}
		});
		this.changed(value);
		this.walletsChange.emit(value);
	}

	private trimmedRows(): DeclaredWallet[] {
		return (this.rows.getRawValue() as DeclaredWallet[]).map((row) => ({
			network: row?.network ?? '',
			address: String(row?.address ?? '').trim(),
		}));
	}

	private createRow(wallet: DeclaredWallet = this.emptyRow()): UntypedFormGroup {
		return this.fb.group({
			network: [wallet.network ?? ''],
			address: [wallet.address ?? ''],
		});
	}

	private emptyRow(): DeclaredWallet {
		return { network: '', address: '' };
	}
}
